// HOTS: which screen a sitting opens in.
//
// Reads the launch payload, stamps the theme, and hands off by ROLE. The two screens share only
// the frame and the question bank - a teacher's sitting saves nothing, awards nothing, and lets
// them move between questions freely - so they live apart rather than as branches in one file.

import { AppContext } from '../../../context/context';
import { decryptData } from '../../../utils/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ls } from '../../../localStorage';
import { applyTheme, Theme } from '../theme';
import { Loader } from '../Loader';
import HotsForStudents from './HotsForStudents';
import HotsForTeachers from './HotsForTeachers';
import { Sitting } from './sitting';

export function Hots() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY = queryParams.get('query');
  const lang = queryParams.get('lang') || 'en';
  const state: any = useContext(AppContext);

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [subTopicSkills, setSubTopicSkills] = useState<string[]>([]);
  const [grade, setGrade] = useState<number | undefined>(undefined);
  // Rise only. A Rise teacher has no classroom unit plan to resolve a curriculum from, so the
  // launcher names the curriculum instead. Absent for B2B, which keeps the unit-plan path.
  const [curriculumId, setCurriculumId] = useState<string | undefined>(undefined);
  const [sitting, setSitting] = useState<Sitting>('Mastery');

  // Decrypted during render, not in the effect below, because the THEME comes out of it and has
  // to be stamped before the first paint - see applyTheme just below.
  const launch = useMemo(() => {
    try {
      return QUERY ? decryptData(QUERY) : null;
    } catch {
      return null;   // reported by the effect, which owns the redirect
    }
  }, [QUERY]);

  // HOTS always renders as blueprint. It is not a preference and does not travel in the
  // payload: the look is part of what marks these questions out from ordinary practice.
  //
  // Painted during the first render, not in an effect. This component returns a Loader before
  // HotsPlayer ever mounts, and until the theme is stamped that loader resolves against space's
  // defaults - the child would see a space loader hand over to a blueprint one.
  useMemo(() => applyTheme(Theme.Blueprint), []);

  useEffect(() => {
    try {
      if (!launch) throw new Error('Missing or unreadable query');
      const query: {
        currentUser: any;
        subTopicSkills: string[];
        grade: number;
        curriculumId?: string;
        sitting: Sitting;
      } = launch;

      const user = query.currentUser;
      if (!user?.token || !user?.dbToken) throw new Error('Invalid session');
      ls.setToken(user.token);
      ls.setDBToken(user.dbToken);

      setCurrentUser(user);
      setSubTopicSkills(query.subTopicSkills || []);
      setGrade(query.grade);
      setCurriculumId(query.curriculumId || undefined);
      setSitting(query.sitting === 'Practice' ? 'Practice' : 'Mastery');
    } catch (error) {
      console.log('hots: could not read launch payload', error);
      state?.navigateTo?.('/pageNotFound');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentUser || subTopicSkills.length === 0) return <Loader theme={Theme.Blueprint} />;

  // Role decides the screen, the way AdaptivePractice.tsx does. The two flows share only the
  // frame and the bank: a teacher's sitting saves nothing, awards nothing, and lets them move
  // between questions freely, so keeping them apart is what keeps each one readable.
  //
  // BOTH roles are named, and anything else falls through to the loader below. A default of
  // "student" would put a parent, an admin or a mistyped role into the SCORED flow, writing
  // attempt rows and coins against whoever happens to be signed in.
  if (currentUser.role === 'student') {
    return (
      <HotsForStudents
        currentUser={currentUser}
        subTopicSkills={subTopicSkills}
        grade={grade}
        sitting={sitting}
        lang={lang}
      />
    );
  }

  if (currentUser.role === 'teacher') {
    return (
      <HotsForTeachers
        currentUser={currentUser}
        subTopicSkills={subTopicSkills}
        grade={grade}
        curriculumId={curriculumId}
        platform={queryParams.get('platform') || ''}
        lang={lang}
      />
    );
  }

  // A role with no HOTS screen. Nothing to serve, and nothing to say to whoever is looking -
  // the launcher should not have opened this.
  return <Loader theme={Theme.Blueprint} />;
}

export default Hots;
